import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet, NavigationStart } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { SideBarComponent } from './CommonComponents/side-bar/side-bar.component';
import { HeaderComponent } from './CommonComponents/header/header.component';
import { LoginComponent } from './Components/login/login.component';
import { MainDrawerComponent } from './CommonComponents/main-drawer/main-drawer.component';
import { BackgroundOverlayDrawerComponent } from './CommonComponents/background-overlay-drawer/background-overlay-drawer.component';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccessTokenService } from './Services/access-token.service';
import { AnalyticsService } from './Services/analytics.service';
import { UserDataProviderService } from './Services/user-data-provider.service';
import { ThemeService } from './Services/theme.service';
import { TenantConfigService } from './Services/tenant-config.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_LANG, SUPPORTED_LANG_CODES, getStoredLang } from './Shared/constants/supported-languages';

export const PUBLIC_PATHS = ['/login', '/forgot-password', '/new-password', '/company-onboarding', '/okta/callback'];

export function isPublicUrl(url: string): boolean {
  return PUBLIC_PATHS.some(p => url === p || url.startsWith(p + '/'));
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, HeaderComponent, SideBarComponent, LoginComponent, MainDrawerComponent, BackgroundOverlayDrawerComponent,
    RouterLink, RouterLinkActive, MatDialogModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Spendkey';
  isShowHeader = false;
  isShowSidenav = false;
  isSidebarCollapsed = true;
  opened = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  aMenuList = [
    {
      title: 'Upload Spend',
      icon: 'arrow_upward',
      link: 'upload',
      selected: false
    },
    {
      title: 'Budget',
      icon: 'sync_alt',
      link: 'budget',
      selected: false
    },
    {
      title: 'Map Spend Data',
      icon: 'sync_alt',
      link: 'MapSpendData',
      selected: false
    },
    {
      title: 'Dashboard',
      icon: 'bar_chart',
      link: 'dashboard',
      selected: false
    },
    {
      title: 'User',
      icon: 'person_add',
      link: 'user',
      selected: false
    }
  ];

  aNoHeaderPages = ['/', '/login', '/error', '/signup', '/forgot-password', '/change-password', '/new-password', '/two-factor-auth', '/company-onboarding'];

  constructor(
    private routerService: Router,
    private userDataProviderService: UserDataProviderService,
    private accessTokenService: AccessTokenService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private translate: TranslateService,
    private themeService: ThemeService,
    private tenantConfig: TenantConfigService,
    private analytics: AnalyticsService
  ) {

    // Apply persisted theme to <html data-theme="..."> before any view renders
    // so first paint already shows the correct brand colors.
    this.themeService.init();

    // Same reason as the theme above: the rail reads its width from a CSS
    // custom property, so apply the tenant's persisted override before the
    // first paint rather than letting it jump when getSettings resolves.
    this.tenantConfig.initSidebarMetrics();

    this.analytics.init();
    const uid = this.accessTokenService.getUserId();
    if (uid) this.analytics.setUser(uid, this.accessTokenService.getUserRole());

    this.routerService.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this._destroying$))
      .subscribe((e) => {
        const url = (e as NavigationEnd).urlAfterRedirects;
        this.analytics.trackPageView(url, url);
      });

    this.userDataProviderService.isShowSidebar.subscribe((isShowSidenavVal: boolean) => {
      this.isShowSidenav = isShowSidenavVal;
      this.isShowHeader = isShowSidenavVal;
    });

    this.accessTokenService.castValue.subscribe((value: boolean) => {
      this.isSidebarCollapsed = value;
    });

    if (this.accessTokenService.getTokenFromLocalStorage() && !this.accessTokenService.isTokenExpired()) {
      this.isShowSidenav = true;
      this.isShowHeader = true;
    }

    this.routerService.events
      .pipe(filter(e => e instanceof NavigationStart), takeUntil(this._destroying$))
      .subscribe((e) => {
        const url = (e as NavigationStart).url.split('?')[0];
        if (!isPublicUrl(url) && !(this.accessTokenService.getTokenFromLocalStorage() && !this.accessTokenService.isTokenExpired())) {
          this.routerService.navigate(['login']).then(() => this.accessTokenService.clearTokens());
        }
      });

    this.msalService.handleRedirectObservable().subscribe((res) => {
      this.accessTokenService.showLoginUI = false;
      if (res) {
        this.accessTokenService.getUserSSOAccessTokenAndProceed(res, 'Microsoft')
          .subscribe({
            next: (response) => {
              if (response.body?.status === 'success') {
                this.accessTokenService.processLogin(response);
                this.analytics.setUser(this.accessTokenService.getUserId(), this.accessTokenService.getUserRole());
                this.analytics.trackLogin('sso-microsoft');
                this.userDataProviderService.isShowSidebar.emit(true);
              } else {
                this.rejectSsoLogin('sso_unauthorized');
              }
            },
            error: () => this.rejectSsoLogin('sso_error'),
          });
      } else {
        // console.log("No redirect interaction detected.");
      }
    });

    // this.setLoginDisplay();

    this.msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((_result: EventMessage) => {
        this.setLoginDisplay();
        if (this.msalService.instance.getAllAccounts().length === 0) {
          this.routerService.navigate(['/login']);
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  ngOnInit(): void {
    this.translate.addLangs(SUPPORTED_LANG_CODES);
    this.translate.setDefaultLang(DEFAULT_LANG);
    this.translate.use(getStoredLang());
  }

  private rejectSsoLogin(reason: string): void {
    this.analytics.trackLoginFailed('sso-microsoft', reason);
    this.accessTokenService.showLoginUI = true;
    this.routerService.navigateByUrl('/login?error=UNAUTHORIZED_SSO_LOGIN');
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  checkUserData() {
    if (!this.accessTokenService.isTokenAvailable()) {
      this.routerService.navigate(['login']).then(() => {
        this.accessTokenService.clearTokens();
        // window.location.reload();
      })
    }
  }

  ToggleSidenav() {
    this.opened = !this.opened;
  }

  selectMenuAndRoute(menuLink: string) {
    this.selectMenuWithLink(menuLink);
    this.routeToLink(menuLink);
  }

  selectMenuWithLink(link: string) {
    this.aMenuList.forEach((item) => {
      if (item.link == link) {
        item.selected = true;
      } else {
        item.selected = false;
      }
    });
  }
  routeToLink(link: string) {
    this.routerService.navigateByUrl(link)
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
